import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Star, Calendar } from "lucide-react";

export default function SyndicatActualites() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", image_url: "", important: false });

  const load = async () => {
    try {
      const { data } = await api.get("/syndicat/actualites");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/syndicat/actualites", form);
      toast.success("Actualité publiée");
      setOpen(false); setForm({ title: "", content: "", image_url: "", important: false }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette actualité ?")) return;
    try { await api.delete(`/syndicat/actualites/${id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Communication"
        title="Actualités"
        sub="Publiez des actualités visibles par les salariés des entreprises adhérentes."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="actualite-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Nouvelle actualité</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Publier une actualité</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="actualite-form">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="actualite-title" className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Contenu</Label>
                  <Textarea required rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="actualite-content" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">URL Image (optionnel)</Label>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="actualite-image" placeholder="https://…" className="h-11" />
                </div>
                <div className="flex items-center gap-2 p-3 border border-[#FFD700] bg-[#FFFBEB]">
                  <input type="checkbox" id="important" checked={form.important} onChange={(e) => setForm({ ...form, important: e.target.checked })} data-testid="actualite-important" className="h-4 w-4 accent-[#FFD700]" />
                  <label htmlFor="important" className="text-xs font-semibold cursor-pointer flex items-center gap-1.5"><Star className="h-3 w-3 text-[#FFD700]" /> Marquer comme important</label>
                </div>
                <DialogFooter><Button type="submit" data-testid="actualite-submit" className="bg-[#002FA7] text-white">Publier</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="space-y-4" data-testid="actualites-list">
          {items.length === 0 ? (
            <div className="bg-white border border-[#DEE2E6] p-10 text-center text-sm text-[#868E96]">
              <Megaphone className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucune actualité. Cliquez sur "Nouvelle actualité".
            </div>
          ) : items.map((a) => (
            <div key={a.id} className={`bg-white border ${a.important ? "border-[#FFD700] border-2" : "border-[#DEE2E6]"} p-5`} data-testid={`actualite-${a.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {a.important && <span className="text-[9px] uppercase tracking-wider font-bold bg-[#FFD700] text-[#0A0A0A] px-1.5 py-0.5 flex items-center gap-1"><Star className="h-2.5 w-2.5" /> Important</span>}
                    <span className="text-[10px] text-[#868E96] flex items-center gap-1.5 font-mono"><Calendar className="h-3 w-3" /> {new Date(a.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold tracking-tight">{a.title}</h3>
                  <div className="text-sm text-[#495057] mt-2 whitespace-pre-wrap">{a.content}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(a.id)} data-testid={`actualite-delete-${a.id}`} className="border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
